export interface IntegrationField {
  field_key: string;
  label: string;
  type: string;
  is_required: boolean;
  default_value?: string;
  options?: string[];
  field_mapping?: Record<string, string>;
  description?: string;
}


export interface Product {
  id?: string;
  _id?: string;
  name: string;
  logo_url?: string;
  tenant_id?: string;
  status?: string;
  integration_fields?: IntegrationField[];
  tags?: Record<string, string>;
  type?: 'saas' | 'logger';
  logger_config?: {
    template_name: string;
    queue_size: string;
    max_disk_space: string;
    worker_threads: string;
    filename: string;
    status: string;
    keywords: LoggerKeyword[];
    soar_mapping?: Record<string, string>;
  };
  created_at?: string;
  updated_at?: string;
}

export interface ProductFormData {
  name: string;
  logo_url?: string;
  tags?: Record<string, string>;
  integration_fields: IntegrationField[];
  type?: 'saas' | 'logger';
  logger_config?: {
    template_name: string;
    queue_size: string;
    max_disk_space: string;
    worker_threads: string;
    filename: string;
    status: string;
    keywords: LoggerKeyword[];
    soar_mapping?: Record<string, string>;
  };
}

export enum TenantLevel {
  L0 = 'L0',
  L1 = 'L1',
  L2 = 'L2',
  L3 = 'L3'
}

export enum TenantType {
  ORG = 'org',
  PARTNER = 'partner'
}

export enum TenantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending'
}

export enum TenantGroup {
  EVENTUS = 'Eventus',
  EVENTUS_UAT = 'Eventus UAT',
  MMAE = 'MMAE',
  BLUE_OCEAN = 'Blue Ocean',
  INSIGHTS = 'Insights',
  SEND_IOC = 'Send Ioc'
}

export enum ScanAction {
  LOGGING = 'logging',
  BLOCK_QUARANTINE = 'block/quarantine'
}

export interface Tenant {
  id: string;
  uuid: string;
  name: string;
  tenant_code: string;
  tenant_type: TenantType;
  level: TenantLevel;
  logo_url?: string;
  group?: TenantGroup;
  parent_tenant_id?: string;
  status: TenantStatus;
  is_deleted?: boolean;
  start_date?: string;
  end_date?: string;
  onboarding_date?: string;
  contract_renewal_status?: string;
  threshold_per_day?: number;
  online_retention?: number;
  offline_retention?: number;
  email_to?: string;
  email_cc?: string;
  from_email?: string;  // Added from_email field
  soar_api_key?: string;
  soar_api_url?: string;
  soar_tenant_id?: string;
  vision_one_url?: string;
  vision_one_api_key?: string;
  destination?: string;
  provider_name?: string;
  scan_action?: ScanAction;
  attached_ioc?: boolean;
  masking_ioc?: boolean;
  sweeping_on_vision_one?: boolean;
  sweeping_on_esdl?: boolean;
  created_by?: string;
  created_at: string;
  updated_at?: string;
  parent_tenant?: Tenant;
  children?: Tenant[];
  metadata?: any;
  settings?: any;
}

export interface TenantFormData extends Omit<Tenant, 'id' | 'created_at' | 'updated_at' | 'parent_tenant' | 'children'> {
  id?: string;
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  READONLY = 'readonly',
  MANAGER = 'manager'
}

export interface User {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole | string;
  tenant_id: string;
  status: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
  tenant?: Tenant;
  last_login?: string;
  // Optional list of regions this user should be synced to
  sync_regions?: string[];
}

export interface UserFormData extends Omit<User, 'id' | 'created_at' | 'updated_at' | 'tenant'> {
  id?: string;
  password?: string;
}

export interface AuthUser extends Omit<User, 'tenant'> {
  tenant_level?: TenantLevel | string;
  token?: string;
  permissions?: string[];
}

export interface LoginFormData {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: AuthUser;
  token: string;
  access_token?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Logger Types
export interface LoggerKeyword {
  keyword: string;
  condition: string;
}

export interface LoggerProduct {
  id?: string;
  product_name: string;
  product_type: string;
  log_type: string;
  template_name: string;
  queue_size: string;
  max_disk_space: string;
  worker_threads: string;
  filename: string;
  status: 'active' | 'inactive';
  keywords: LoggerKeyword[];
  created_at?: string;
  updated_at?: string;
}

export interface LoggerProductFormData {
  product_name: string;
  product_type: string;
  log_type: string;
  template_name: string;
  queue_size: string;
  max_disk_space: string;
  worker_threads: string;
  filename: string;
  status: 'active' | 'inactive';
  keywords: LoggerKeyword[];
}

export interface LoggerCustomer {
  id?: string;
  customer_id: string;
  target: string;
  status: 'active' | 'inactive';
  logger_products: string[]; // Array of logger product IDs
  logger_type?: string;
  tls_config?: {
    cacert?: string;
    mycert?: string;
    myprivkey?: string;
    permittedpeer?: string;
  };
  additional_configs?: {
    enableCyberArk?: boolean;
    conditions?: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface LoggerCustomerFormData {
  customer_id: string;
  target: string;
  status: 'active' | 'inactive';
  logger_products: string[];
  logger_type?: string;
  tls_cacert?: string;
  tls_mycert?: string;
  tls_myprivkey?: string;
  tls_permittedpeer?: string;
  // Deprecated fields - kept for backward compatibility
  enable_cyberark?: boolean;
  additional_conditions?: string;
}

export interface ConfigFileTemplate {
  template_content: string;
  customer_name: string;
  logger_type: string;
  filename: string;
}

export interface LoggerProductIntegration {
  id?: string;
  customer_id: string;
  product_id: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Helper function for safely formatting error messages
export const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error.message === 'string') return error.message;
  return 'An error occurred';
}; 